<template>
  <v-card class="rounded-card card-shadow mt-3">
    <v-card-text class="text--primary">
      <v-row dense>
        <v-col cols="12" sm="8">
          <px-autocomplete-produto dense
                                   required
                                   show-details
                                   menu-props="auto"
                                   class="required rounded-tag-small"
                                   base-path="/produtos/page"
                                   v-model="produto.produto"
                                   :id-fornecedor="idFornecedor"
                                   :only-cotavel="false"
                                   :truncate-value="60"
                                   :disabled="readonly"
                                   :error="$v.produto.produto.$error"
                                   @blur="$v.produto.produto.$touch()"/>
        </v-col>
        <v-col cols="12" sm="2">
          <px-autocomplete-unidade-medida dense
                                          required
                                          :disabled="readonly"
                                          class="required rounded-tag-small"
                                          v-model="produto.unidadeMedida"
                                          :onlyOpen="false"
                                          :error="$v.produto.unidadeMedida.$error"
                                          @blur="$v.produto.unidadeMedida.$touch()"/>
        </v-col>
        <v-col cols="12" sm="2">
          <px-date-picker dense
                          label="Data de faturamento"
                          input-class="rounded-tag-small"
                          :disabled="readonly"
                          v-model="produto.dataFaturamento"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="3">
          <px-input-decimal-number
            dense
            required
            label="Quantidade"
            class="required rounded-tag-small"
            :precision="precision"
            :error="$v.produto.quantidade.$error"
            @blur="$v.produto.quantidade.$touch()"
            :disabled="readonly"
            v-model="produto.quantidade"/>
        </v-col>
        <v-col cols="3">
          <px-input-currency
            dense
            label="Preço unitário"
            class="required rounded-tag-small"
            ref="precoUnitario"
            v-model="produto.precoUnitario"
            :options="configuracaoMoeda"
            :precision="precision"
            :disabled="readonly"
            :error="$v.produto.precoUnitario.$error"
            @blur="$v.produto.precoUnitario.$touch()"/>
        </v-col>
        <v-col cols="3">
          <px-input-percentual
            dense
            show-suffix
            label="ICMS"
            input-class="required rounded-tag-small"
            ref="icms"
            :error="$v.produto.impostos.icms.$error"
            @blur="$v.produto.impostos.icms.$touch()"
            :disabled="readonly"
            v-model="produto.impostos.icms"/>
        </v-col>
        <v-col cols="3">
          <px-input-percentual
            dense
            show-suffix
            label="IPI"
            input-class="required rounded-tag-small"
            ref="ipi"
            :error="$v.produto.impostos.ipi.$error"
            @blur="$v.produto.impostos.ipi.$touch()"
            :disabled="readonly"
            v-model="produto.impostos.ipi"/>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" sm="10">
          <v-textarea
            v-if="isEditing && isFromCotacao"
            dense
            hide-details
            outlined
            auto-grow
            :disabled="readonly"
            v-model="produto.especificacaoProdutoOrigem"
            label="Especificação"
            rows="1"
            class="rounded-tag-small"
          />
          <v-textarea
            v-else
            dense
            hide-details
            outlined
            auto-grow
            :disabled="readonly"
            v-model="produto.especificacaoProduto"
            label="Especificação"
            rows="1"
            class="rounded-tag-small"
          />
        </v-col>
        <v-col cols="12" sm="2">
          <px-cadastro-produto-observacoes
            @file-loading="onFileLoad"
            :produto="produto"
            :readonly="readonly"
            :small="false"/>
          <px-cadastro-produto-campos-adicionais
            show-cadastros-adicionais
            show-nota-entrada
            dense
            ref="camposAdicionais"
            :campos-obrigatorios="camposObrigatorios"
            :campos-readonly="camposReadonly"
            :produto="produto"
            :small="false"
            :readonly="readonly"
            :campos-hidden="camposHidden"
            :cadastros-adicionais="produto.cadastrosAdicionais"
            :min-data-previsao-entrega="compra.dataCompra"
            :is-from-requisicao="isFromRequisicao"
            @replicar-campo="onReplicarCampo"/>
          <v-tooltip top v-if="canRemove && !hideAcoes">
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click="onRemove">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Remover item</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-card-text>
    <px-confirm-dialog ref="confirm"/>
  </v-card>
</template>

<script>
import { isEqual } from 'lodash';
import { required } from 'vuelidate/lib/validators';
import validationMixin from '@/mixins/validation-mixin';
import { OrigemCompra, SituacaoItemCompra } from 'px-business-components';
import {
  computeFromProduto,
  validateCamposProdutoErp,
  initialStateCamposProdutoErp,
  hasCadastrosAdicionaisProdutoErp,
} from '@/modules/produtos/campos-erp/helpers/validate-campos-erp';
import { getPrecision } from '@/utils/produto-utils';
import validationFileMixin from '@/mixins/validation-file-mixin';
import {
  initialStateImpostos,
  mapValoresTabelaPreco,
  initialStateValoresConvertido,
} from '@/modules/compra/cadastro/compra-utils';
import { formatToCode } from '@/utils/moment-date-utils';
import { isSameDate } from '@/utils/date-utils';
import moment from 'moment';
import TabelaPrecoService from '@/modules/tabela-preco/tabela-preco-service';
import moedaMixin from '@/mixins/moeda-mixin';
import { mergeCadastrosAdicionais, extrairCadastrosAdicionais } from '@/utils/cadastros-adicionais-utils';
import TiposEntidadeCadastroAdicional from '@/enums/tipos-entidade-cadastro-adicional';

export default {
  mixins: [validationMixin, validationFileMixin, moedaMixin],
  data() {
    return {
      initUnidadeMedida: true,
    };
  },
  props: {
    compra: {
      type: Object,
      required: true,
    },
    produto: {
      type: Object,
      required: true,
    },
    erpCamposIntegracao: String,
    camposObrigatorios: Object,
    isEditing: Boolean,
    isAVista: Boolean,
    isRascunho: Boolean,
    camposReadonly: Object,
    isFromRequisicao: Boolean,
    hideAcoes: Boolean,
    camposHidden: Object,
  },
  computed: {
    moeda() {
      return this.compra.moeda;
    },
    precision() {
      return getPrecision(this.produto.produto);
    },
    isFromCotacao() {
      return this.compra.origemCompra === OrigemCompra.keys.COTACAO.key;
    },
    canRemove() {
      return !this.produto.id || this.isRascunho;
    },
    readonly() {
      return this.isItemCancelado;
    },
    isItemCancelado() {
      return this.produto.situacao === SituacaoItemCompra.keys.CANCELADO.key;
    },
    idFornecedor() {
      return this.compra.fornecedor ? this.compra.fornecedor.id : null;
    },
  },
  methods: {
    naoNecessitaComputarCadastrosAdicionais() {
      return typeof this.produto.cadastrosAdicionais === 'object'
        && hasCadastrosAdicionaisProdutoErp(
          this.erpCamposIntegracao,
          this.produto.cadastrosAdicionais,
        );
    },
    validateCadastrosAdicionais() {
      const [isValid, message] = validateCamposProdutoErp(
        this.erpCamposIntegracao,
        this.produto.cadastrosAdicionais,
        this.produto.produto,
        this.produto.situacao,
        'COMPRA',
      );
      if (!isValid) {
        this.$toast(message, { color: 'error' });
      }
      return isValid;
    },
    onRemove() {
      this.$refs.confirm
        .open(
          'Você deseja remover este produto da compra?',
          'O produto será removido e esta operação não pode ser desfeita.',
          { width: 450 },
        )
        .then((confirm) => {
          if (!confirm) {
            return;
          }
          this.$emit('remove');
        });
    },
    validate() {
      const isInvalid = [
        this.validateForm(),
        this.$refs.camposAdicionais.validate(),
        !this.validateCadastrosAdicionais(),
        this.validateFile(),
      ].some(v => v);
      return !isInvalid;
    },
    previous() {
      this.$emit('previous');
    },
    inicializarValorMoedaConvertidos() {
      if (!this.produto.valorMoedaConvertido) {
        this.produto.valorMoedaConvertido = initialStateValoresConvertido();
      }
    },
    inicializarImpostos() {
      if (!this.produto.impostos) {
        this.produto.impostos = initialStateImpostos();
      }
      this.setarIcmsProduto();
      this.setarIpiProduto();
    },
    setarIcmsProduto() {
      if (this.produto.impostos.icms > 0 || !this.produto.produto) {
        return;
      }
      this.produto.impostos.icms = this.produto.produto?.icms ?? 0;
    },
    setarIpiProduto() {
      if (this.produto.impostos.ipi > 0 || !this.produto.produto) {
        return;
      }
      this.produto.impostos.ipi = this.produto.produto?.ipi ?? 0;
    },
    carregarUnidadeMedida() {
      const produtoUnidadeMedida = this.produto.produto?.unidadeMedida;
      this.produto.unidadeMedida = this.produto.unidadeMedida?.id
        ? this.produto.unidadeMedida : produtoUnidadeMedida ?? null;
    },
    inicializarUnidadeMedida() {
      if (this.initUnidadeMedida) {
        this.carregarUnidadeMedida();
        this.initUnidadeMedida = false;
        return;
      }
      if (this.produto.produto) {
        const { unidadeMedida } = this.produto.produto;
        this.produto.unidadeMedida = { ...unidadeMedida };
      }
    },
    getCadastrosAdicionais(produto) {
      const mainCadastrosAdicionais = extrairCadastrosAdicionais(
        produto,
        TiposEntidadeCadastroAdicional.keys.COMPRA_PRODUTO.key,
      );
      const produtoCadastrosAdicionais = produto.produto ? produto.produto.cadastrosAdicionais : [];
      return mergeCadastrosAdicionais(mainCadastrosAdicionais, produtoCadastrosAdicionais);
    },
    inicializarCadastrosAdicionais() {
      if (this.naoNecessitaComputarCadastrosAdicionais()) {
        return;
      }
      const cadastrosAdicionais = this.getCadastrosAdicionais(this.produto);
      if (!cadastrosAdicionais && !cadastrosAdicionais.length) {
        this.produto.cadastrosAdicionais = initialStateCamposProdutoErp(this.erpCamposIntegracao);
        return;
      }
      this.produto.cadastrosAdicionais = computeFromProduto(
        this.erpCamposIntegracao,
        cadastrosAdicionais,
      );
    },
    inicializarAoSelecionarProduto(newProduto) {
      if (!newProduto) {
        return;
      }
      const {
        especificacao,
        arquivos,
        observacaoExterna,
        observacaoInterna,
      } = newProduto;

      this.produto.especificacaoProduto = especificacao ?? this.produto.especificacaoProduto;
      this.produto.arquivos = arquivos ?? this.produto.arquivos;
      this.produto.observacaoExterna = this.produto.observacaoExterna ?? observacaoExterna;
      this.produto.observacaoInterna = this.produto.observacaoInterna ?? observacaoInterna;
      this.produto.finalidadeCompra = this.getFinalidadeCompraFromProduto(this.produto);

      this.inicializarUnidadeMedida();
      this.inicializarImpostos();
      this.inicializarCadastrosAdicionais();
      this.getTabelaPrecoItem();
    },
    getFinalidadeCompraFromProduto(produto) {
      if (!produto.produto.finalidadeCompra) {
        return produto.finalidadeCompra ?? this.compra.finalidadeCompra;
      }
      return produto.produto.finalidadeCompra;
    },
    getTabelaPrecoItem() {
      if (!this.compra.fornecedor || !this.produto.produto || this.isEditing) {
        return;
      }
      const dataAtual = formatToCode(moment());
      const query = `fornecedor.id = ${this.compra.fornecedor.id} and ativo = true and produto.id = ${this.produto.produto.id} and dataVigenciaInicial <= ${dataAtual} and dataVigenciaFinal >= ${dataAtual}`;
      TabelaPrecoService.getItensTabelaPreco({
        query,
      })
        .then(({ data }) => {
          if (!data || !data.length) {
            return;
          }
          const produtoFromTabela = mapValoresTabelaPreco(this.produto, data[0], this.isAVista);
          this.produto.$$itemTabelaPreco = produtoFromTabela.$$itemTabelaPreco;
          this.produto.precoUnitario = produtoFromTabela.precoUnitario;
          this.produto.impostos = produtoFromTabela.impostos;
        });
    },
    onReplicarCampo(type) {
      this.$emit('replicar-campo', type);
    },
  },
  created() {
    this.inicializarCadastrosAdicionais();
  },
  watch: {
    'produto.produto': function (newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      if (!newValue) {
        return;
      }
      // reseta o item da tabela se trocar o produto
      this.produto.$$itemTabelaPreco = null;
      this.inicializarAoSelecionarProduto(newValue);
    },
    'compra.tipoNotaEntrada': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      const oldTipoNotaEntradaId = oldValue ? oldValue.id : null;
      if (!this.produto.tipoNotaEntrada
        || this.produto.tipoNotaEntrada.id === oldTipoNotaEntradaId) {
        this.produto.tipoNotaEntrada = newValue;
      }
    },
    'compra.centroCusto': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      const oldCentroCustoId = oldValue ? oldValue.id : null;
      if (!this.produto.centroCusto || this.produto.centroCusto.id === oldCentroCustoId) {
        this.produto.centroCusto = newValue;
      }
    },
    'compra.projeto': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      const oldProjetoId = oldValue ? oldValue.id : null;
      if (!this.produto.projeto || this.produto.projeto.id === oldProjetoId) {
        this.produto.projeto = newValue;
      }
    },
    'compra.finalidadeCompra': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      if (!this.produto.finalidadeCompra || isEqual(this.produto.finalidadeCompra, oldValue)) {
        this.produto.finalidadeCompra = newValue;
      }
    },
    'compra.dataPrevisaoEntrega': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      if (!this.produto.dataPrevisaoEntrega
        || isSameDate(this.produto.dataPrevisaoEntrega, oldValue)) {
        this.produto.dataPrevisaoEntrega = newValue;
      }
    },
    'compra.dataFaturamento': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      if (!this.produto.dataFaturamento || isSameDate(this.produto.dataFaturamento, oldValue)) {
        this.produto.dataFaturamento = newValue;
      }
    },
  },
  validations: {
    produto: {
      produto: {
        required,
      },
      quantidade: {
        required,
      },
      unidadeMedida: {
        required,
      },
      precoUnitario: {
        required,
        minValue(value) {
          return value > 0;
        },
      },
      impostos: {
        icms: {
          required,
        },
        ipi: {
          required,
        },
      },
    },
  },
};
</script>
